import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, CheckCircle, SlidersHorizontal, Activity } from 'lucide-react';

/**
 * RebalancerTuningPanel — operator knobs for the in-cluster engine rebalancer.
 *
 * Backend (already shipped + live-verified):
 *   GET  /v1/clusters/{cid}/node-optimization/rebalance-config
 *        → { engine_rebalancer: { min_nodes_to_consider, min_node_age_minutes,
 *            savings_threshold_usd, savings_threshold_pct }, ... }
 *        An ABSENT key means "use the rebalancer's env/helm default" — we leave
 *        that input BLANK so its placeholder (the default) shows.
 *   POST /v1/clusters/{cid}/node-optimization/rebalance-config
 *        accepts FLAT keys, only-provided-keys-updated:
 *          engine_min_nodes_to_consider (int)
 *          engine_min_node_age_minutes  (int)
 *          engine_savings_threshold_usd (float)
 *          engine_savings_threshold_pct (float 0..1)
 *
 * The engine writes a cluster-scoped singleton RebalancerConfig CRD that the
 * in-cluster rebalancer WATCHES and applies live. We do NOT touch the CRD or
 * backend contract here — UI only.
 *
 * Percent unit conversion: the API stores a 0..1 FRACTION; operators think in
 * whole percent. We show/edit whole percent and convert at the boundaries
 * (GET fraction → ×100 for the input; POST input → ÷100 for the fraction).
 *
 * Empty input === "unset" === fall back to the rebalancer default (placeholder).
 */

// Defaults shown as PLACEHOLDER text (never prefilled as values). These mirror
// the rebalancer env/helm defaults so the operator sees what "blank" resolves
// to without us pretending the value is explicitly set.
const DEFAULTS = {
  minNodes: 2,
  minNodeAge: 5,   // minutes
  savingsUsd: 50,  // $/month
  savingsPct: 15,  // whole percent in the UI (API fraction 0.15)
};

// Field descriptors drive both rendering and the GET→form / form→POST mapping.
// `apiGetKey` is the nested key under engine_rebalancer; `apiPostKey` is the
// flat key the POST body expects. `fromApi`/`toApi` handle the pct fraction.
const FIELDS = [
  {
    key: 'minNodes',
    label: 'Min nodes to consider',
    hint: 'only consolidate when the cluster has at least this many nodes',
    apiGetKey: 'min_nodes_to_consider',
    apiPostKey: 'engine_min_nodes_to_consider',
    placeholder: String(DEFAULTS.minNodes),
    inputProps: { type: 'number', min: 1, step: 1 },
    integer: true,
    fromApi: (v) => String(v),
    toApi: (s) => parseInt(s, 10),
    validate: (n) => Number.isInteger(n) && n >= 1,
    invalidMsg: 'must be an integer ≥ 1',
  },
  {
    key: 'minNodeAge',
    label: 'Min node age (minutes)',
    hint: 'give new nodes this long to settle first',
    apiGetKey: 'min_node_age_minutes',
    apiPostKey: 'engine_min_node_age_minutes',
    placeholder: String(DEFAULTS.minNodeAge),
    inputProps: { type: 'number', min: 0, step: 1 },
    integer: true,
    fromApi: (v) => String(v),
    toApi: (s) => parseInt(s, 10),
    validate: (n) => Number.isInteger(n) && n >= 0,
    invalidMsg: 'must be an integer ≥ 0',
  },
  {
    key: 'savingsUsd',
    label: 'Min savings ($/month)',
    hint: 'skip moves that save less than this per month',
    apiGetKey: 'savings_threshold_usd',
    apiPostKey: 'engine_savings_threshold_usd',
    placeholder: String(DEFAULTS.savingsUsd),
    inputProps: { type: 'number', min: 0, step: 'any' },
    integer: false,
    fromApi: (v) => String(v),
    toApi: (s) => parseFloat(s),
    validate: (n) => Number.isFinite(n) && n >= 0,
    invalidMsg: 'must be a number ≥ 0',
  },
  {
    key: 'savingsPct',
    label: 'Min savings (%)',
    hint: 'skip consolidations saving less than this share of node cost',
    apiGetKey: 'savings_threshold_pct',
    apiPostKey: 'engine_savings_threshold_pct',
    placeholder: String(DEFAULTS.savingsPct),
    inputProps: { type: 'number', min: 0, max: 100, step: 'any' },
    integer: false,
    // API fraction (0..1) → whole percent for the input. Round-trip-friendly:
    // 0.15 → "15", 0.075 → "7.5".
    fromApi: (v) => String(Number((v * 100).toFixed(6))),
    // whole percent → API fraction (0..1).
    toApi: (s) => parseFloat(s) / 100,
    validate: (n) => Number.isFinite(n) && n >= 0 && n <= 100,
    invalidMsg: 'must be a percent between 0 and 100',
  },
];

const blankForm = () => FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

// Human labels for the backend's applied/effective state token. Raw token
// survives only inside title=. Unknown states read as Active.
const APPLIED_LABELS = {
  reconciled: 'Active',
  applied: 'Active',
  active: 'Active',
  pending: 'Applying…',
  error: 'Not applied',
};
const appliedLabel = (raw) =>
  APPLIED_LABELS[String(raw).toLowerCase()] || 'Active';

// "3 min ago" style relative time for the applied-at stamp (full timestamp
// stays in title=).
const relativeTime = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return d.toLocaleDateString();
};

const RebalancerTuningPanel = ({ clusterId }) => {
  // Each form field is a STRING (controlled input). '' === unset === default.
  const [form, setForm] = useState(blankForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // Effective/applied status surfaced from the GET, if the backend includes it
  // so the operator can confirm the in-cluster loop picked the config up.
  const [applied, setApplied] = useState(null);

  // Pull the in-cluster loop's effective/applied status from whatever shape the
  // backend uses. We accept several conservative key names rather than guess a
  // single one — absent → we simply show nothing.
  const extractApplied = (data) => {
    if (!data || typeof data !== 'object') return null;
    const candidate =
      data.applied ??
      data.effective ??
      data.status ??
      data.engine_rebalancer?.applied ??
      data.engine_rebalancer?.effective ??
      data.engine_rebalancer?.status ??
      null;
    if (candidate == null) return null;
    if (typeof candidate === 'string') return { label: candidate };
    if (typeof candidate === 'object') {
      // Common fields the loop might report. All optional.
      return {
        label: candidate.phase || candidate.state || candidate.message || null,
        observedGeneration: candidate.observed_generation ?? candidate.observedGeneration,
        appliedAt: candidate.applied_at ?? candidate.appliedAt ?? candidate.updated_at,
        raw: candidate,
      };
    }
    return null;
  };

  const fetchConfig = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`,
      );
      const er = res.data?.engine_rebalancer || {};
      // Prefill ONLY keys the backend returned. Absent key → leave blank so the
      // placeholder (default) shows.
      const next = blankForm();
      for (const f of FIELDS) {
        const v = er[f.apiGetKey];
        if (v !== undefined && v !== null) {
          next[f.key] = f.fromApi(v);
        }
      }
      setForm(next);
      setApplied(extractApplied(res.data));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const update = (key) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((f) => ({ ...f, [key]: v }));
    setSaved(false);
  };

  // Per-field client validation for the filled inputs. Returns a map of
  // key→message for any field that's non-empty AND invalid.
  const fieldErrors = FIELDS.reduce((acc, f) => {
    const raw = form[f.key];
    if (raw === '' || raw == null) return acc; // blank is valid (= default)
    const n = f.integer ? parseInt(raw, 10) : parseFloat(raw);
    if (Number.isNaN(n) || !f.validate(n)) acc[f.key] = f.invalidMsg;
    return acc;
  }, {});
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    // Build a FLAT payload with ONLY filled fields, using the POST keys and the
    // pct fraction conversion. Blank fields are omitted so the backend keeps
    // (or falls back to) the existing default for them.
    const payload = {};
    for (const f of FIELDS) {
      const raw = form[f.key];
      if (raw === '' || raw == null) continue;
      payload[f.apiPostKey] = f.toApi(raw);
    }
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`,
        payload,
      );
      setSaved(true);
      // Surface fresh applied/effective status if the POST echoes it; otherwise
      // re-read so the operator sees the in-cluster loop's pickup.
      const postApplied = extractApplied(res.data);
      if (postApplied) {
        setApplied(postApplied);
      } else {
        await fetchConfig();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!clusterId) return null;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <span title="Stored per cluster and applied live in-cluster (RebalancerConfig)">
            <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
          </span>
          Rebalancer Tuning
          {applied?.label && (
            <Badge
              className="bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1"
              title={`Reported state: ${applied.label}`}
            >
              <Activity className="h-3 w-3" />
              {appliedLabel(applied.label)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">
          Tune when this cluster consolidates nodes. Blank fields use the
          defaults shown.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading settings
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs" htmlFor={`rebal_${f.key}`}>
                    {f.label}
                  </Label>
                  <Input
                    id={`rebal_${f.key}`}
                    {...f.inputProps}
                    value={form[f.key]}
                    onChange={update(f.key)}
                    placeholder={f.placeholder}
                    aria-invalid={!!fieldErrors[f.key]}
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Default: {f.placeholder}
                    {f.hint ? ` · ${f.hint}` : ''}
                  </p>
                  {fieldErrors[f.key] && (
                    <p className="text-[10px] text-red-600 mt-0.5">
                      {fieldErrors[f.key]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {applied?.appliedAt && relativeTime(applied.appliedAt) && (
              <div className="mt-3 text-[11px] text-slate-500">
                <span
                  title={`${new Date(applied.appliedAt).toLocaleString()}${
                    applied.observedGeneration != null
                      ? ` · generation ${applied.observedGeneration}`
                      : ''
                  }`}
                >
                  Applied {relativeTime(applied.appliedAt)}
                </span>
              </div>
            )}

            {error && <p className="text-xs text-red-600 mt-3">{error}</p>}

            <div className="mt-4 flex items-center gap-3">
              <Button onClick={handleSave} disabled={saving || hasFieldErrors}>
                {saving ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…
                  </>
                ) : (
                  'Save thresholds'
                )}
              </Button>
              {saved && !saving && (
                <span className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Saved. Changes take effect within a minute.
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RebalancerTuningPanel;
